import { useParams, Link } from "react-router-dom";
import { ARTICLES } from "@/data/articles.mjs";
import Seo from "@/lib/seo/Seo";
import { ArrowLeft, ArrowRight, Clock, ChevronRight } from "lucide-react";
import CTASection from "@/components/CTASection";
import NotFound from "@/pages/NotFound";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import Illustrations from "@/components/illustrations/FintechIllustrations";

const HERO_ILLUSTRATIONS = {
  "how-to-read-a-merchant-statement": Illustrations.ResourcesIllustration,
  "cash-discount-vs-traditional": Illustrations.CashDiscountHeroIllustration,
  "questions-before-choosing-a-processor": Illustrations.ProcessIllustration,
};

export default function BlogArticle() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) return <NotFound />;

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);
  const canonical = `https://swipetronics.com/resources/${article.slug}`;
  const HeroIllustration =
    HERO_ILLUSTRATIONS[article.slug] || Illustrations.ResourcesIllustration;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.seoDescription || article.excerpt,
    author: { "@type": "Organization", name: "Swipetronics" },
    publisher: { "@type": "Organization", name: "Swipetronics" },
    mainEntityOfPage: canonical,
  };

  return (
    <div data-testid="blog-article-page">
      <Seo
        title={article.seoTitle}
        description={article.seoDescription || article.excerpt}
        canonical={canonical}
        ogType="article"
        jsonLd={articleJsonLd}
      />

      {/* ── Unified Hero ───────────────────────────────── */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)" }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)" }} />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/resources" className="hover:text-white">Resources</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">{article.category}</span>
          </nav>

          <div className="grid lg:grid-cols-[1.15fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  {article.category}
                </div>
                <h1 className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3.5rem] xl:text-[3.75rem] font-extrabold leading-[1.05] text-white tracking-[-0.02em]">
                  {article.title}
                </h1>
                <p className="mt-6 text-[16px] md:text-[17px] text-white/75 leading-[1.7] max-w-xl">
                  {article.excerpt}
                </p>
                <div className="mt-7 flex items-center gap-4 text-[13px] text-white/60">
                  <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {article.readTime} read</span>
                  <span>By the Swipetronics Team</span>
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <HeroIllustration
                  transparent
                  className="relative w-full h-auto max-w-[520px] mx-auto drop-shadow-[0_30px_60px_rgba(0,0,0,0.35)]"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="container-swipe py-12 md:py-16">
        <div className="grid lg:grid-cols-[1fr,300px] gap-10">
          <article>
            <Link to="/resources" className="text-sm text-slate-500 hover:text-[var(--swipe-accent)] inline-flex items-center gap-1.5 mb-6">
              <ArrowLeft className="w-4 h-4" /> All articles
            </Link>
            <div className="prose-swipe space-y-8">
              {article.body.map(([heading, body]) => (
                <div key={heading}>
                  <h2 className="font-display-tight text-2xl sm:text-3xl font-black text-[var(--swipe-navy)] mb-3">{heading}</h2>
                  <p className="text-slate-600 leading-relaxed text-[17px] whitespace-pre-line">{body}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-[var(--swipe-muted)] rounded-2xl p-7 border border-line">
              <div className="overline mb-3">Take the next step</div>
              <h3 className="font-display-tight text-2xl font-black text-[var(--swipe-navy)]">Want to apply this to your own business?</h3>
              <p className="mt-3 text-slate-600">Request a free, no-pressure savings analysis. We&apos;ll compare your current setup with what Swipetronics can offer.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/savings-analysis" className="btn-primary-swipe text-sm py-2.5">Free Savings Analysis <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/contact" className="btn-outline-swipe text-sm py-2.5">Contact us</Link>
              </div>
            </div>
          </article>

          <aside className="space-y-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Related articles</div>
            {related.map((r) => (
              <Link key={r.slug} to={`/resources/${r.slug}`} className="block bg-white border border-line rounded-xl p-5 hover-lift group">
                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--swipe-accent)]">{r.category}</div>
                <div className="mt-1.5 font-display font-bold text-[var(--swipe-navy)] leading-snug group-hover:text-[var(--swipe-accent)]">{r.title}</div>
                <div className="mt-2 text-xs text-slate-500 flex items-center gap-1.5"><Clock className="w-3 h-3" /> {r.readTime}</div>
              </Link>
            ))}
          </aside>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
